import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Printer, Download, Loader2 } from 'lucide-react';
import CargoArrivalNoticePrint from './CargoArrivalNoticePrint';

const CANPrint = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch CAN document data
        const canResponse = await fetch(
          `${backendUrl}/api/dockets/${docketId}/documents/CARGO_ARRIVAL_NOTICE`,
          { headers: getAuthHeaders() }
        );

        if (!canResponse.ok) {
          throw new Error('CAN document not found. Please save the form first.');
        }

        const canData = await canResponse.json();
        setFormData(canData.document_data);

        // Fetch company/docket data
        const docketResponse = await fetch(
          `${backendUrl}/api/dockets/${docketId}/print-data`,
          { headers: getAuthHeaders() }
        );

        if (docketResponse.ok) {
          const docketData = await docketResponse.json();
          if (docketData.company) {
            setCompanyData(docketData.company);
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (docketId) fetchData();
  }, [docketId, backendUrl]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center print:hidden">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center print:hidden gap-4">
        <p className="text-text-secondary">{error || 'Document not found.'}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Print styles — same pattern as DocketPrint, must live in this component */}
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            min-height: 0 !important;
          }
          body * { visibility: hidden; }
          #can-print-content, #can-print-content * { visibility: visible; }
          #can-print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .bg-gray-50 {
            background-color: #f9fafb !important;
            -webkit-print-color-adjust: exact !important;
          }
          table, td, th { border-color: black !important; }
        }
        @media screen {
          #can-print-content {
            max-width: 210mm;
            margin: 0 auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>

      {/* Print controls — hidden when printing */}
      <div className="print:hidden bg-white border-b sticky top-0 z-10">
        <div className="max-w-[900px] mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadPDF} data-testid="can-save-pdf-btn">
              <Download className="w-4 h-4 mr-2" />
              Save as PDF
            </Button>
            <Button onClick={handlePrint} data-testid="can-print-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Content — id="can-print-content" lives HERE (same component as <style> and window.print) */}
      <div className="min-h-screen bg-gray-100 print:bg-white print:min-h-0">
        <div id="can-print-content">
          <CargoArrivalNoticePrint
            docketId={docketId}
            formData={formData}
            companyData={companyData}
          />
        </div>
      </div>
    </>
  );
};

export default CANPrint;
